import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from "next-themes";
import { Menu, X, ArrowUpRight, Sun, Moon } from "lucide-react";
import { scrollToId } from "./Reveal";

const LINKS = [
  { label: "Home", id: "hero" },
  { label: "Projects", id: "projects" },
  { label: "Services", id: "services" },
  { label: "Experience", id: "experience" },
  { label: "Contact", id: "contact" },
];

const ThemeToggle = ({ testid = "theme-toggle" }) => {
  const { resolvedTheme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const isDark = resolvedTheme === "dark";
  return (
    <button
      onClick={() => setTheme(isDark ? "light" : "dark")}
      className="w-10 h-10 rounded-full glass flex items-center justify-center text-ink hover:text-brand transition-colors duration-300"
      aria-label="Toggle theme"
      data-testid={testid}
    >
      {mounted && isDark ? <Sun className="w-[18px] h-[18px]" /> : <Moon className="w-[18px] h-[18px]" />}
    </button>
  );
};

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(id), 350);
    } else {
      scrollToId(id);
    }
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-3 left-0 right-0 z-50 px-4"
      data-testid="main-nav"
    >
      <div className={`max-w-6xl mx-auto rounded-full px-4 sm:px-6 h-16 flex items-center justify-between transition-all duration-300 ${scrolled ? "glass shadow-lg shadow-brand/5" : "border border-transparent"}`}>
        <button onClick={() => go("hero")} className="font-display font-extrabold text-lg text-ink tracking-tight pl-2" data-testid="nav-logo">
          Ricky <span className="text-gradient">Sokhi</span>
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              className="relative text-sm font-medium text-ink-soft hover:text-ink transition-colors duration-200 group"
              data-testid={`nav-link-${l.label.toLowerCase()}`}
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-0.5 w-0 rounded-full btn-gradient transition-[width] duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <ThemeToggle />
          <button
            onClick={() => go("contact")}
            className="inline-flex items-center gap-2 rounded-full btn-gradient pl-5 pr-4 py-2.5 text-sm font-semibold hover:opacity-90 transition-opacity"
            data-testid="nav-cta"
          >
            Hire me <ArrowUpRight className="w-4 h-4" />
          </button>
        </div>

        <div className="md:hidden flex items-center gap-2">
          <ThemeToggle testid="theme-toggle-mobile" />
          <button className="text-ink p-2" onClick={() => setOpen((o) => !o)} data-testid="nav-mobile-toggle" aria-label="Toggle menu">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden overflow-hidden glass rounded-3xl mt-2 max-w-6xl mx-auto"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {LINKS.map((l) => (
                <button key={l.id} onClick={() => go(l.id)} className="text-left text-lg font-display font-semibold text-ink" data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}>
                  {l.label}
                </button>
              ))}
              <button onClick={() => go("contact")} className="mt-2 inline-flex items-center justify-center gap-2 rounded-full btn-gradient px-5 py-3 text-sm font-semibold" data-testid="nav-mobile-cta">
                Hire me <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
